import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { useSnackbar } from 'notistack';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getLabelMessage, getMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components';
import { getDetail } from 'features/views/campaignSettings/campaignDetail/selectors';
import startupCurve from 'features/views/campaignSettings/startupCurve';

import CampaignInfo from './components/CampaignInfo';
import CurveTypeRadioGroup from './components/CurveTypeRadioGroup';
import WeekList from './components/WeekList';

export const StartupCurve = () => {
	const dispatch = useDispatch();
	const { campaignId } = useParams();
	const { enqueueSnackbar } = useSnackbar();

	const campaignDetail = useSelector(getDetail);
	const isPristine = useSelector(startupCurve.selectors.getIsPristine);

	const handleReset = useCallback(async () => {
		const success = await dispatch(startupCurve.actions.resetStartupCurve(campaignDetail?.id));

		if (success) {
			dispatch(startupCurve.actions.resetWeeks());
			enqueueSnackbar(getMessage('toastr:approveResetStartupCurve'), { variant: 'success' });
		}
	}, [campaignDetail.id, dispatch, enqueueSnackbar]);

	const handleSave = useCallback(async () => {
		const success = await dispatch(startupCurve.actions.saveStartupCurve(campaignDetail.id));

		if (success) {
			enqueueSnackbar(getMessage('toastr:approveSaveStartupCurve'), { variant: 'success' });
		}
	}, [dispatch, campaignDetail.id, enqueueSnackbar]);

	return (
		<Stack spacing={3}>
			<CampaignInfo campaignDetail={campaignDetail} />
			<CurveTypeRadioGroup />
			<WeekList campaignId={!isNilOrEmpty(campaignId) && Number(campaignId)} />
			<Grid container>
				<Grid size={12}>
					<Grid
						container
						spacing={2}
						sx={{
							justifyContent: 'flex-end',
						}}
					>
						<Grid size={4}>
							<ComponentLoader fetchStorePath={startupCurve.constants.fetchState.reset} ignoreErrors>
								<Button fullWidth variant="contained" onClick={handleReset}>
									{getLabelMessage('startupCurve:reset')}
								</Button>
							</ComponentLoader>
						</Grid>
						<Grid size={4}>
							<ComponentLoader fetchStorePath={startupCurve.constants.fetchState.save} ignoreErrors>
								<Button fullWidth variant="contained" onClick={handleSave} disabled={isPristine}>
									{getLabelMessage('common:save')}
								</Button>
							</ComponentLoader>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Stack>
	);
};

StartupCurve.propTypes = {
	match: PropTypes.shape({
		params: PropTypes.shape({
			campaignId: PropTypes.string,
		}),
	}),
};

// if campaignDetail is not loaded yet we still need campaign id
export default StartupCurve;
